import { z } from "zod";

// ── ripenn_search_ai_divergence ──────────────────────────────────────

export const SearchDivergenceItem = z.object({
  url: z.string(),
  gscClicks90d: z.number(),
  gscImpressions90d: z.number(),
  aiCitationRate: z.number().nullable(), // 0-1 ratio; null = not in AI tracking scope
  topCompetitorCited: z.string().nullable(),
  sessions90d: z.number(),
});
export type SearchDivergenceItem = z.infer<typeof SearchDivergenceItem>;

export const SearchDivergenceResponse = z.object({
  items: z.array(SearchDivergenceItem),
  sourceStatus: z.enum(["gsc_connected", "no_gsc"]),
  note: z.string().optional(),
  computedAt: z.string(),
});
export type SearchDivergenceResponse = z.infer<typeof SearchDivergenceResponse>;

// ── ripenn_topic_signals ─────────────────────────────────────────────

export const TopicSignalsResponse = z.object({
  signals: z.object({
    /** URL topics: share of runs that cited the page (registry citationRate). */
    aiCitationRate: z.number().optional(),
    /** Prompt topics: share of runs that mentioned the brand (registry mentionRate). */
    mentionRate: z.number().optional(),
    gscClicks90d: z.number().optional(),
    gscImpressions90d: z.number().optional(),
    sessions90d: z.number().optional(),
    conversions90d: z.number().optional(),
    revenue90d: z.number().optional(),
  }),
  matchedPrompts: z.array(
    z.object({
      id: z.string(),
      text: z.string(),
      mentionRate: z.number(),
    }),
  ),
  matchedPages: z.array(
    z.object({
      url: z.string(),
      title: z.string().optional(),
    }),
  ),
  source: z.object({
    ai: z.boolean(),
    gsc: z.boolean(),
    ga4: z.boolean(),
  }),
  estimatedFields: z.array(z.string()),
  lastMeasuredAt: z.string().nullable(),
  computedStatus: z.enum(["measured", "estimated", "no_data"]),
});
export type TopicSignalsResponse = z.infer<typeof TopicSignalsResponse>;

// ── ripenn_underoptimized_content ────────────────────────────────────

/**
 * Wire shape of GET /api/visibility/content-stats.
 *
 * Built from `get_project_page_citations`, the per-page scan-level
 * producer: a content row's `overallRate` is pageCitationRate (scans
 * citing the page over scans of the prompts it surfaced in, summed
 * across engines) over the 30 days ending today, and `previousRate` is
 * the same rate over the 30 days before, read as its own window. A page,
 * engine or project with no scans in that previous window has a null
 * `previousRate` and a null `trend` (no made-up 0% and "up"); `trend`
 * otherwise comes from the registry's `computeTrend`. Every rate here
 * is a 0-1 ratio rounded to three places (pageCitationRateOf), the
 * same value and scale the page header shows. Each `engines[]`
 * entry carries
 * `citedCount` = scans citing the page and `totalChecks` = scans, per
 * engine. `sourceUrl` comes from the content table (null when the row
 * vanished between the two reads).
 *
 * `.passthrough()` keeps unrelated additions from breaking validation,
 * while a missing consumed key fails loudly (ContractError) instead of
 * being silently defaulted.
 */
export const ContentStatsEngineStat = z
  .object({
    engine: z.string().optional(),
    // Scans of the page's prompts by this engine over the last 30 days.
    totalChecks: z.number(),
    // Scans among them in which the page was cited.
    citedCount: z.number().optional(),
    citationRate: z.number().optional(),
    // Null when the engine had no scans of the page the 30 days before.
    previousRate: z.number().nullable().optional(),
    trend: z.enum(["up", "down", "stable"]).nullable().optional(),
  })
  .passthrough();
export type ContentStatsEngineStat = z.infer<typeof ContentStatsEngineStat>;

export const ContentStatsContentRow = z
  .object({
    contentId: z.string(),
    title: z.string(),
    slug: z.string().nullable().optional(),
    status: z.string().optional(),
    sourceUrl: z.string().nullable(),
    // 0-1 page citation rate over the last 30 days.
    overallRate: z.number(),
    // The same rate over the 30 days before that; null when the page
    // had no scans then.
    previousRate: z.number().nullable().optional(),
    trend: z.enum(["up", "down", "stable"]).nullable().optional(),
    lastChecked: z.string().nullable(),
    engines: z.array(ContentStatsEngineStat).nullable(),
  })
  .passthrough();
export type ContentStatsContentRow = z.infer<typeof ContentStatsContentRow>;

export const ContentVisibilityStatsResponse = z
  .object({
    projectId: z.string().optional(),
    overall: z
      .object({
        // Content documents in the project.
        totalContent: z.number(),
        // Documents whose page has at least one scan in the window.
        monitoredContent: z.number(),
        overallRate: z.number().optional(),
        previousRate: z.number().nullable().optional(),
        trend: z.enum(["up", "down", "stable"]).nullable().optional(),
        totalChecks: z.number().optional(),
        lastCheckTime: z.string().nullable().optional(),
      })
      .passthrough(),
    engines: z.array(ContentStatsEngineStat).optional(),
    content: z.array(ContentStatsContentRow),
  })
  .passthrough();
export type ContentVisibilityStatsResponse = z.infer<
  typeof ContentVisibilityStatsResponse
>;

export const UnderoptimizedContentItem = z.object({
  contentId: z.string(),
  title: z.string(),
  sourceUrl: z.string(),
  citationRate: z.number(), // 0-1 ratio, 30-day window
  totalChecks: z.number(),
  lastCheckedAt: z.string().nullable(),
});
export type UnderoptimizedContentItem = z.infer<
  typeof UnderoptimizedContentItem
>;

export const UnderoptimizedContentResponse = z.object({
  items: z.array(UnderoptimizedContentItem),
  totalInventory: z.number(),
  monitoredCount: z.number(),
  note: z.string().optional(),
  computedStatus: z.enum(["measured", "no_data"]),
  dataWindowDays: z.literal(30),
});
export type UnderoptimizedContentResponse = z.infer<
  typeof UnderoptimizedContentResponse
>;

// ── ripenn_audit_page ────────────────────────────────────────────────

export const PageAuditResponse = z.object({
  url: z.string(),
  canonicalUrl: z.string(),
  inventoryStatus: z.enum(["in_inventory", "inactive", "not_in_inventory"]),
  aiCitationRate: z.number().nullable(), // 0-100; null if no data
  totalChecks: z.number(),
  citedByEngines: z.array(z.string()),
  competitorsCited: z.array(
    z.object({ domain: z.string(), citationRate: z.number() }),
  ),
  linkedPrompts: z.array(
    z.object({
      id: z.string(),
      text: z.string(),
      category: z.string().nullable(),
    }),
  ),
  gscClicks90d: z.number().nullable(),
  gscImpressions90d: z.number().nullable(),
  sessions90d: z.number().nullable(),
  structuralNotes: z.array(z.string()),
  lastMeasuredAt: z.string().nullable(),
  computedStatus: z.enum(["measured", "no_data"]),
});
export type PageAuditResponse = z.infer<typeof PageAuditResponse>;

// ── ripenn_data_sources ──────────────────────────────────────────────

const SourceStatus = z.object({
  connected: z.boolean(),
  lastSyncedAt: z.string().nullable(),
  staleDays: z.number().nullable(),
});

export const DataSourcesResponse = z.object({
  sources: z.object({
    ai: SourceStatus.extend({
      connected: z.literal(true),
      lastCheckedAt: z.string().nullable(),
    }),
    gsc: SourceStatus.extend({
      siteUrl: z.string().nullable(),
      lastSyncError: z.string().nullable(),
    }),
    ga4: SourceStatus.extend({
      propertyId: z.string().nullable(),
      propertyDisplayName: z.string().nullable(),
      lastSyncError: z.string().nullable(),
    }),
    shopify: SourceStatus.extend({
      storeDomain: z.string().nullable(),
    }),
  }),
  groundedSignals: z.array(z.string()),
  estimatedSignals: z.array(z.string()),
  computedAt: z.string(),
});
export type DataSourcesResponse = z.infer<typeof DataSourcesResponse>;

// ── ripenn_analytics_opportunities ──────────────────────────────────

export const AnalyticsOpportunitiesEnvelope = z.object({
  items: z.array(
    z.object({
      opportunity_type: z.string(),
      page_url: z.string(),
      sessions_90d: z.number(),
      conversions_90d: z.number(),
      revenue_90d: z.number(),
      score: z.number(),
      details: z.record(z.unknown()).optional(),
    }),
  ),
  totalFound: z.number(),
  computedStatus: z.enum(["measured", "no_data"]),
  note: z.string().optional(),
});
export type AnalyticsOpportunitiesEnvelope = z.infer<
  typeof AnalyticsOpportunitiesEnvelope
>;

// ── ripenn_trigger_scan ──────────────────────────────────────────────

export const ScanTriggerResponse = z.discriminatedUnion("status", [
  z.object({
    status: z.literal("confirmation_required"),
    staleDays: z.number().nullable(),
    staleThresholdDays: z.number(),
    estimatedDurationMinutes: z.number(),
    message: z.string(),
  }),
  z.object({
    status: z.literal("running"),
    jobId: z.string(),
    estimatedDurationMinutes: z.number(),
    message: z.string(),
  }),
]);
export type ScanTriggerResponse = z.infer<typeof ScanTriggerResponse>;

// ── POST /api/visibility/scan/trigger ───────────────────────────────
// Wire-level response for the MCP-facing scan trigger route.

export const ScanTriggerRouteResponse = z.object({
  workflowId: z.string(),
  refreshedSources: z.array(z.string()),
});
export type ScanTriggerRouteResponse = z.infer<typeof ScanTriggerRouteResponse>;

// ── ripenn_scan_status ───────────────────────────────────────────────

export const ScanStatusMcpResponse = z.object({
  status: z.string(),
  progress: z
    .object({
      percentage: z.number().optional(),
      completedChecks: z.number().optional(),
      totalChecks: z.number().optional(),
      currentEngine: z.string().optional(),
    })
    .optional(),
  completedAt: z.string().optional(),
  error: z.string().optional(),
});
export type ScanStatusMcpResponse = z.infer<typeof ScanStatusMcpResponse>;

// ── ripenn_delete_prompts ────────────────────────────────────────────
// Deleting is destructive and MCP clients can pass many ids at once, so
// the tool mirrors ripenn_trigger_scan: without confirm:true it returns
// the prompts that would be deleted; with confirm:true it deletes them.

export const PromptsDeletePreviewItem = z.object({
  id: z.string(),
  prompt: z.string(),
  category: z.string(),
  isActive: z.boolean(),
});
export type PromptsDeletePreviewItem = z.infer<typeof PromptsDeletePreviewItem>;

export const PromptsDeleteMcpResponse = z.discriminatedUnion("status", [
  z.object({
    status: z.literal("confirmation_required"),
    prompts: z.array(PromptsDeletePreviewItem),
    /** Ids not found in the project; nothing will be deleted for these. */
    unknownIds: z.array(z.string()),
    message: z.string(),
  }),
  z.object({
    status: z.literal("deleted"),
    deletedCount: z.number(),
    message: z.string(),
  }),
]);
export type PromptsDeleteMcpResponse = z.infer<typeof PromptsDeleteMcpResponse>;
